import { MapPin, CheckCircle, Star, Shield } from "lucide-react";

export default function ServiceAreasSection() {
  const areas = [
    {
      city: "Paradise Valley",
      state: "Arizona",
      description: "Arizona's most prestigious residential enclave",
      zipCodes: ["85253", "85254", "85255"],
      propertiesServed: 450,
      highlight: true,
    },
    {
      city: "Scottsdale",
      state: "Arizona",
      description: "The West's Most Western Town",
      zipCodes: [
        "85260", "85250", "85251", "85252", "85253", "85254", "85255", "85256",
        "85257", "85258", "85259", "85261", "85262", "85263",
        "85264", "85266", "85267", "85268", "85269", "85271"
      ],
      propertiesServed: 680,
      highlight: false,
    },
  ];

  return (
    <section
      id="service-areas"
      className="py-28 lg:py-36 bg-white"
      data-testid="service-areas-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
            Proudly Serving Arizona
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1628] mb-6">
            Our Service
            <br />
            <span className="gradient-text">Areas</span>
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto text-lg">
            From Paradise Valley's exclusive estates to Scottsdale's vibrant
            golf communities, we're your trusted local experts for premium tree
            care.
          </p>
        </div>

        {/* Service Areas Grid */}
        <div className="grid md:grid-cols-2 gap-10 mb-20">
          {areas.map((area, index) => (
            <div
              key={index}
              className={`bg-[#F8F6F1] p-8 border-t-4 ${
                area.highlight ? "border-[#D4A853]" : "border-[#10B981]"
              } hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300`}
              data-testid={`service-area-${area.city.toLowerCase().replace(" ", "-")}`}
            >
              {area.highlight && (
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4A853] to-[#B8923A] text-[#0A1628] px-4 py-2 text-xs font-bold uppercase tracking-wider mb-6">
                  <Star className="w-3 h-3" />
                  Primary Service Area
                </div>
              )}

              <div className="flex items-start gap-5 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#0A1628]">
                    {area.city}
                  </h3>
                  <p className="text-[#64748B] text-sm">{area.state}</p>
                </div>
              </div>

              <p className="text-[#64748B] mb-6 text-lg">{area.description}</p>

              {/* Stats */}
              <div className="flex items-center gap-8 mb-8 pb-8 border-b border-[#10B981]/10">
                <div>
                  <p className="font-['Cormorant_Garamond'] text-3xl font-bold gradient-text">
                    {area.propertiesServed}+
                  </p>
                  <p className="text-[#64748B] text-xs uppercase tracking-wider">
                    Properties Served
                  </p>
                </div>
                <div>
                  <p className="font-['Cormorant_Garamond'] text-3xl font-bold gradient-text">
                    {area.zipCodes.length}
                  </p>
                  <p className="text-[#64748B] text-xs uppercase tracking-wider">
                    Zip Codes
                  </p>
                </div>
              </div>

              {/* Zip Codes */}
              <div>
                <p className="text-sm font-bold text-[#0A1628] mb-4">
                  Zip Codes We Serve:
                </p>
                <div className="flex flex-wrap gap-2">
                  {area.zipCodes.slice(0, 10).map((zip, idx) => (
                    <span
                      key={idx}
                      className="bg-white text-[#0A1628] px-4 py-2 text-sm border border-[#10B981]/20"
                    >
                      {zip}
                    </span>
                  ))}
                  {area.zipCodes.length > 10 && (
                    <span className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white px-4 py-2 text-sm font-semibold">
                      +{area.zipCodes.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why Local Matters */}
        <div className="bg-gradient-to-r from-[#F8F6F1] to-white p-10 lg:p-14 border-l-4 border-[#10B981]">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#0A1628] mb-6">
                Why Choose a Local Expert?
              </h3>
              <p className="text-[#64748B] mb-6 text-lg">
                We understand the unique challenges of Arizona's desert climate.
                From monsoon damage response to understanding the specific needs
                of desert-adapted trees, our local expertise ensures your
                property receives the best possible care.
              </p>
              <div className="space-y-4">
                {[
                  "Deep knowledge of Arizona's native and adapted tree species",
                  "Rapid response during monsoon emergencies",
                  "Understanding of HOA requirements in local communities",
                  "Relationships with local golf courses and resorts",
                ].map((point, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                    <span className="text-[#64748B]">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center lg:text-right">
              <div className="inline-flex items-center gap-4 p-6 bg-white shadow-xl shadow-emerald-500/5 border border-[#10B981]/10">
                {/* Arizona State Flag */}
                <svg
                  viewBox="0 0 60 40"
                  className="w-16 h-11"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="60" height="40" fill="#002868"/>
                  <polygon points="0,0 30,20 60,0" fill="#BF0A30"/>
                  <g fill="#FED700">
                    <polygon points="30,20 26,9 34,9"/>
                    <polygon points="30,20 22,12 38,12"/>
                    <polygon points="30,20 18,8 42,8"/>
                    <polygon points="30,20 16,6 44,6"/>
                  </g>
                  <circle cx="30" cy="20" r="5" fill="#FED700"/>
                </svg>
                <div className="text-left">
                  <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.2em] uppercase mb-1">
                    Locally Owned
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#0A1628]">
                    & Operated
                  </p>
                  <p className="text-[#64748B] text-sm">Paradise Valley, Arizona</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
